import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useRoom } from '../context/RoomContext';

import socketService from '../services/socket';
import { roomsAPI } from '../services/api';
import VideoGrid from '../components/VideoGrid';
import Controls from '../components/Controls';
import Chat from '../components/Chat';
import ParticipantsList from '../components/ParticipantsList';
import MeetingMenu from '../components/MeetingMenu';
import SettingsModal from '../components/SettingsModal';
import ScreenShareNotification from '../components/ScreenShareNotification';
import WaitingRoomPanel from '../components/WaitingRoomPanel';
import Logo from '../assets/images/LOGO.png';
import './Room.css';

import { ContactRound, Users, MessageCircleMore, Loader } from 'lucide-react';

const Room = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { room, setRoom, participants, setParticipants, messages, isConnected, waitingRoomUsers, joinRoom, startRoomCall, initializeMedia, sendMessage } = useRoom();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isWaitingRoomOpen, setIsWaitingRoomOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Extract navigation state for host identification
  const location = useLocation();
  const navState = location.state || {};

  // Track unread messages when chat is closed
  useEffect(() => {
    if (!isChatOpen && messages.length > 0) {
      setUnreadMessages((prev) => prev + 1);
    }
  }, [messages.length, isChatOpen]);

  // Reset unread count when chat opens
  useEffect(() => {
    if (isChatOpen) {
      setUnreadMessages(0);
    }
  }, [isChatOpen]);

  // Redirect if not in a room
  useEffect(() => {
    if (!room && isConnected) {
      const performJoin = async () => {
        // Get user data from navigation state (from salle.js) or fallback to sessionStorage/localStorage
        const userId = navState.userId || (() => {
          const userJson = sessionStorage.getItem('user');
          let userObj = null;
          try { userObj = userJson ? JSON.parse(userJson) : null; } catch { }
          return userObj?.id;
        })();
        
        const userName = navState.userName || (() => {
          const storedHostName = sessionStorage.getItem('hostName');
          const userJson = sessionStorage.getItem('user');
          let userObj = null;
          try { userObj = userJson ? JSON.parse(userJson) : null; } catch { }
          return storedHostName || userObj?.name || userObj?.email || localStorage.getItem('userName') || 'User';
        })();
        
        // Determine if this user is the host (starting the call vs joining)
        const isHost = navState.isHost !== undefined ? navState.isHost : sessionStorage.getItem('isHost') === 'true';

        // 1. Try joining via Socket first - use appropriate method based on host status
        try {
          if (isHost) {
            // Starting a new call - use startRoomCall to become host
            await startRoomCall(roomId, userName);
          } else {
            // Joining an existing call - use joinRoom as participant
            await joinRoom(roomId, userName);
          }
          return;
        } catch {
          console.warn('[Room] join-room failed, attempting REST fallback...');

          // 2. Get room from database
          let roomData;
          try {
            const roomRes = await roomsAPI.getRoom(roomId);
            if (roomRes.success) roomData = roomRes.data;
          } catch {
            console.error('[Room] Room not found via REST');
            navigate('/');
            return;
          }

          // 3. Get participants
          let participantsData = [];
          try {
            const partRes = await roomsAPI.getParticipants(roomId);
            participantsData = partRes.success ? partRes.data : [];
          } catch {
            console.warn('[Room] Could not fetch participants');
            participantsData = [];
          }

          // 4. Populate Room context
          const numericRoomId = roomData.id_meeting || roomData.id;
          setRoom({
            id: numericRoomId,
            name: roomData.name || roomData.nom_salle,
            hostId: socketService.getSocketId(),
            isPublic: roomData.isPublic ?? roomData.is_public ?? true,
            description: roomData.description,
            meeting_code: roomData.meeting_code || null,
            id_meeting: roomData.id_meeting || null,
          });

          // 5. Check if user already added as participant via socket
          if (userId) {
            // Get user object for email/name
            const userJson = sessionStorage.getItem('user');
            let userObj = null;
            try { 
              userObj = userJson ? JSON.parse(userJson) : null; 
            } catch { 
              userObj = null; 
            }
            
            if (!participantsData.some(p => p.id === userId)) {
              try {
                await roomsAPI.addParticipant(numericRoomId, userObj?.email || userId.toString(), isHost ? 'host' : 'member');
                const updatedPartRes = await roomsAPI.getParticipants(numericRoomId);
                if (updatedPartRes.success) {
                  setParticipants(updatedPartRes.data);
                }
              } catch (addErr) {
                if (!(addErr.message && addErr.message.includes('User already'))) {
                  console.warn('[Room] Failed to add participant:', addErr.message);
                }
                try {
                  const partRes = await roomsAPI.getParticipants(numericRoomId);
                  if (partRes.success) {
                    setParticipants(partRes.data);
                  }
                } catch (fetchErr) {
                  console.error('Error loading participants:', fetchErr);
                  setParticipants([]);
                }
              }
            } else {
              setParticipants(participantsData);
            }
          } else {
            setParticipants(participantsData);
          }

          // 6. Initialize media
          try {
            await initializeMedia();
            socketService.requestPeerConnections();
          } catch (mediaErr) {
            console.error('[Room] Media initialization failed:', mediaErr);
          }
        }
      };

      performJoin();
    }
  }, [room, isConnected, navigate, roomId, joinRoom, startRoomCall, initializeMedia, navState]);

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);
  };

  if (!room) {
    return (
      <div className="room-loading">
        <div className="spinner" />
        <p>Loading room...</p>
      </div>
    );
  }

  return (
    <div className="room-container">
      <header className="room-header">
        <div className="room-info">
          <img src={Logo} alt="Logo" style={{ width: '40px', height: '40px', marginRight: '12px' }} />
          <h2 className="room-name">{room.name}</h2>
          <button className="room-id-btn" onClick={copyRoomId} title="Click to copy">
            ID: {roomId.substring(0, 8)}...
            <span className="copy-icon">📋</span>
          </button>
        </div>

        <div className="header-actions">
          {waitingRoomUsers.length > 0 && (
            <button
              className={`header-btn waiting-room-btn ${isWaitingRoomOpen ? 'active' : ''}`}
              onClick={() => setIsWaitingRoomOpen(!isWaitingRoomOpen)}
              title="Salle d'attente"
            >
              <Loader /> {waitingRoomUsers.length}
            </button>
          )}
          <button
            className="header-btn invite-btn"
            onClick={() => setIsInviteOpen(true)}
            title="Inviter un participant"
          >
            <ContactRound /> Inviter
          </button>
          <button
            className={`header-btn ${isParticipantsOpen ? 'active' : ''}`}
            onClick={() => {
              setIsParticipantsOpen(!isParticipantsOpen);
              if (!isParticipantsOpen) setIsChatOpen(false);
            }}
          >
            <Users /> {participants.length}
          </button>
          <button
            className={`header-btn ${isChatOpen ? 'active' : ''}`}
            onClick={() => {
              setIsChatOpen(!isChatOpen);
              if (!isChatOpen) setIsParticipantsOpen(false);
            }}
          >
            <MessageCircleMore />
            {unreadMessages > 0 && (
              <span className="badge">{unreadMessages}</span>
            )}
          </button>
          <MeetingMenu onOpenSettings={() => setIsSettingsOpen(true)} />
        </div>
      </header>

      {/* Screen Share Notifications */}
      <ScreenShareNotification />

      {/* Waiting Room Panel */}
      <WaitingRoomPanel
        isOpen={isWaitingRoomOpen}
        onClose={() => setIsWaitingRoomOpen(false)}
      />

      <main className="room-main">
        <div className="video-section">
          <VideoGrid />
        </div>

        {isParticipantsOpen && (
          <ParticipantsList
            isOpen={isParticipantsOpen}
            onClose={() => setIsParticipantsOpen(false)}
          />
        )}

        {isChatOpen && (
          <Chat
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            messages={messages}
            onSendMessage={sendMessage}
            socketId={socketService.getSocketId()}
          />
        )}

      </main>

      <footer className="room-footer">
        <Controls meetingId={room?.id_meeting || room?.id} />
      </footer>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}

      {/* Invite Modal */}
      {isInviteOpen && (
        <div className="modal-overlay" onClick={() => { setIsInviteOpen(false); setInviteError(''); setInviteSuccess(''); }}>
          <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invite-modal-header">
              <h3>Inviter un participant</h3>
              <button className="close-btn" onClick={() => { setIsInviteOpen(false); setInviteError(''); setInviteSuccess(''); }}>✕</button>
            </div>
            <div className="invite-modal-body">
              <p>Entrez l'adresse email de la personne que vous souhaitez inviter à cette réunion.</p>
              <input
                type="email"
                placeholder="email@gmail.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="invite-email-input"
              />
              {inviteError && <p className="error-text">{inviteError}</p>}
              {inviteSuccess && <p className="success-text">{inviteSuccess}</p>}
            </div>
            <div className="invite-modal-footer">
              <button className="btn-cancel" onClick={() => { setIsInviteOpen(false); setInviteError(''); setInviteSuccess(''); }}>
                Annuler
              </button>
              <button
                className="btn-invite"
                onClick={async () => {
                  if (inviteEmail.trim()) {
                    setInviteLoading(true);
                    setInviteError('');
                    setInviteSuccess('');
                    try {
                      await roomsAPI.sendInvite(roomId, inviteEmail);
                      setInviteSuccess('Invitation envoyée avec succès!');
                      setTimeout(() => {
                        setInviteEmail('');
                        setIsInviteOpen(false);
                        setInviteSuccess('');
                      }, 1500);
                    } catch (err) {
                      setInviteError(err.message || "Échec de l'envoi de l'invitation");
                    } finally {
                      setInviteLoading(false);
                    }
                  }
                }}
                disabled={!inviteEmail.trim() || inviteLoading}
              >
                {inviteLoading ? 'Envoi en cours...' : "Envoyer l'invitation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Room;